import PageHeader from "@/components/site/PageHeader";
import AboutSection from "@/components/site/AboutSection";
import Testimonials from "@/components/site/Testimonials";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "@/lib/seo";

const VALUES = [
    {
        title: "Integritet",
        text: "Vi ställer oss alltid på klientens sida — men aldrig på bekostnad av principerna som definierar juristyrket.",
    },
    {
        title: "Spetskompetens",
        text: "Varje ärende kräver djup kunskap. Vi investerar löpande i vår kompetens så att vi alltid ligger steget före.",
    },
    {
        title: "Personligt engagemang",
        text: "Bakom varje uppdrag står en människa. Vi lyssnar, förklarar och stannar kvar tills saken är avslutad.",
    },
    {
        title: "Diskretion",
        text: "Förtroende är vårt fundament. Information som delas med oss stannar hos oss.",
    },
];

export default function OmOss() {
    useSEO({
        title: "Om oss",
        description:
            "BM Juridik är en familjeledd juristbyrå med sex kontor i Sverige. Personlig, tillgänglig och affärsmässig rådgivning inom tolv rättsområden.",
    });
    return (
        <div data-testid="page-om-oss">
            <PageHeader
                kicker="Om oss"
                title="Din jurist —"
                accent="inte byråns akt."
                                description="BM Juridik är en familjeledd juristbyrå där du aldrig blir ett ärendenummer. Vi är små nog att ge dig vår seniora jurists personliga telefon, och breda nog att företräda dig inom tolv rättsområden — från vårdnadstvisten vid köksbordet till miljonaffären på styrelserummet. Varje uppdrag leds av en namngiven jurist som finns tillgänglig, som ringer upp när löften ges, och som vinner fall genom förberedelse snarare än fraser."
                image="https://images.pexels.com/photos/1529034/pexels-photo-1529034.jpeg"
            />

            <AboutSection />

            <section className="relative py-24 bg-[#0A0A0B] border-t border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="overline">Våra värderingar</p>
                    <h2 className="mt-4 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                        Fyra principer{" "}
                        <span className="italic text-[#C4A977]">
                            som styr
                        </span>{" "}
                        allt vi gör.
                    </h2>

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
                        {VALUES.map((v, i) => (
                            <motion.div
                                key={v.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: i * 0.08 }}
                                data-testid={`value-card-${i}`}
                                className="bg-[#111113] border border-white/5 hover:border-[#C4A977]/40 p-10 rounded-sm transition-colors duration-500"
                            >
                                <p className="font-serif text-5xl text-[#C4A977]/40 leading-none">
                                    0{i + 1}
                                </p>
                                <h3 className="mt-6 font-serif text-3xl text-[#F4F4F5]">
                                    {v.title}
                                </h3>
                                <p className="mt-4 text-[#A1A1AA] leading-relaxed">
                                    {v.text}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <Testimonials />

            <section className="relative py-24 bg-[#0A0A0B] border-t border-white/5">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="overline">Nästa steg</p>
                    <h2 className="mt-4 font-serif font-light text-3xl sm:text-5xl lg:text-6xl leading-[1.1]">
                        Bli en del av vår klientkrets.
                    </h2>
                    <Link
                        to="/kontakt"
                        data-testid="om-oss-cta"
                        className="mt-10 inline-flex items-center gap-3 px-8 py-4 bg-[#C4A977] text-black hover:bg-[#DBC08F] transition-all duration-300 rounded-sm font-medium text-sm tracking-wide group"
                    >
                        Boka en konsultation
                        <ArrowRight
                            size={16}
                            className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                    </Link>
                </div>
            </section>
        </div>
    );
}
